import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Info } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { PageHeader } from '../components/PageHeader';
import { ReadingMeta, SourcesBlock } from '../components/ReadingMeta';
import TableOfContents from '../components/TableOfContents';
import ContactStrip from '../components/ContactStrip';
import SEO from '../components/SEO';
import { SEO_KEYWORDS } from '../data/seo';
import { SITE_URL, CONTACT } from '../data/site';
import { WHY_BENEFITS, POZZOLAN_MECHANISM, CALCINATION_STAGES } from '../data/science';
import AnswerBlock from '../components/geo/AnswerBlock';
import Breadcrumbs from '../components/geo/Breadcrumbs';
import AuthorMeta from '../components/geo/AuthorMeta';
import RelatedTopics from '../components/geo/RelatedTopics';
import FAQ from '../components/geo/FAQ';
import Timeline from '../components/Timeline';
import { FAQS, RELATED, CLUSTER, PAGE_DATES, GEO_LABELS } from '../data/geo';

// Idealised kaolinite formula with proper HTML subscripts (no image).
const KaoliniteFormula = () => (
  <span className="whitespace-nowrap font-medium text-slate-700">Al<sub>2</sub>Si<sub>2</sub>O<sub>5</sub>(OH)<sub>4</sub></span>
);

const Grundlagen = () => {
  const { t, lang } = useLang();
  const L = GEO_LABELS[lang];
  const answer = lang === 'de'
    ? 'Metakaolin ist ein \u00fcberwiegend amorphes, reaktives alumosilikatisches Material, das durch kontrollierte Kalzinierung kaolinitischer Rohstoffe hergestellt wird. In zementgebundenen Systemen kann es als puzzolanischer Zusatzstoff eingesetzt werden.'
    : 'Metakaolin is a predominantly amorphous, reactive aluminosilicate material produced by the controlled calcination of kaolinitic raw materials. In cement-bound systems it can be used as a pozzolanic addition.';

  const toc = [
    { id: 'herkunft', label: t('grundlagen.s1Title') },
    { id: 'thermische-aktivierung', label: t('grundlagen.taTitle') },
    { id: 'dehydroxylierung', label: t('grundlagen.taTocDehydrox') },
    { id: 'kalzinierungsfenster', label: t('grundlagen.taWindowTitle') },
    { id: 'partikelgroesse', label: t('grundlagen.taTocParticle') },
    { id: 'einordnung', label: t('grundlagen.s2Title') },
    { id: 'reaktion', label: t('grundlagen.s3Title') },
    { id: 'warum', label: t('grundlagen.whyTitle') },
    { id: 'historie', label: t('grundlagen.timelineTitle') },
    { id: 'cluster', label: lang === 'de' ? 'Themen im \u00dcberblick' : 'Topics overview' },
    { id: 'faq', label: L.faqTitle },
    { id: 'quellen', label: t('common.sources') },
  ];

  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'TechArticle',
    headline: t('grundlagen.title'), description: answer, inLanguage: lang,
    mainEntityOfPage: `${SITE_URL}/grundlagen`,
    datePublished: PAGE_DATES.grundlagen.published, dateModified: PAGE_DATES.grundlagen.reviewed,
    author: { '@type': 'Organization', name: 'NEWCHEM GmbH', url: CONTACT.website },
    publisher: { '@type': 'Organization', name: 'NEWCHEM GmbH', url: CONTACT.website },
    reviewedBy: { '@type': 'Organization', name: 'NEWCHEM GmbH' },
  };

  return (
    <div>
      <SEO title={t('grundlagen.title')} description={answer} path="/grundlagen" lang={lang} keywords={SEO_KEYWORDS[lang]} jsonLd={jsonLd} />
      <PageHeader label={t('grundlagen.label')} title={t('grundlagen.title')} lead={t('grundlagen.lead')} imageKey="powderBeige" />
      <section className="bg-white py-10 lg:py-14">
        <div className="container-wide">
          <Breadcrumbs items={[{ label: t('grundlagen.label'), to: '/grundlagen' }]} />
        </div>
        <div className="container-wide mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 order-2 lg:order-1">
            <div className="lg:sticky lg:top-24 space-y-5">
              <ReadingMeta minutes={t('grundlagen.readMinutes')} />
              <TableOfContents items={toc} />
              <AuthorMeta dates={PAGE_DATES.grundlagen} />
            </div>
          </aside>
          <article className="lg:col-span-8 order-1 lg:order-2 max-w-2xl">
            <AnswerBlock>{answer}</AnswerBlock>

            <div id="herkunft" className="mt-12 scroll-mt-24">
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900">{t('grundlagen.s1Title')}</h2>
              <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">{t('grundlagen.s1Body')}</p>
            </div>

            {/* Deep-dive: thermal activation of kaolinite */}
            <div id="thermische-aktivierung" className="mt-14 scroll-mt-24">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('grundlagen.taKicker')}</span>
              <h2 className="mt-2 text-[24px] font-extrabold tracking-tight text-slate-900">{t('grundlagen.taTitle')}</h2>
              <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">
                {t('grundlagen.taIntro')}<KaoliniteFormula />.
              </p>

              <h3 id="dehydroxylierung" className="mt-9 scroll-mt-24 text-[16px] font-bold text-slate-900">{t('grundlagen.taDehydroxTitle')}</h3>
              <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{t('grundlagen.taDehydroxBody')}</p>

              <h3 id="kalzinierungsfenster" className="mt-8 scroll-mt-24 text-[16px] font-bold text-slate-900">{t('grundlagen.taWindowTitle')}</h3>
              <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{t('grundlagen.taWindowBody')}</p>

              <div className="mt-5 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-5 py-4" data-testid="overcalcination-note">
                <Info className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-[13.5px] font-bold text-slate-900">{t('grundlagen.taOvercalcTitle')}</p>
                  <p className="mt-1 text-[13.5px] text-slate-600 leading-relaxed">{t('grundlagen.taOvercalcBody')}</p>
                </div>
              </div>

              <h3 id="partikelgroesse" className="mt-9 scroll-mt-24 text-[16px] font-bold text-slate-900">{t('grundlagen.taParticleTitle')}</h3>
              <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{t('grundlagen.taParticleBody')}</p>

              <h3 className="mt-8 text-[16px] font-bold text-slate-900">{t('grundlagen.taReactionTitle')}</h3>
              <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{t('grundlagen.taReactionBody')}</p>

              <h3 className="mt-9 mb-4 text-[15px] font-bold text-slate-900">{t('grundlagen.taProcessTitle')}</h3>
              <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="calcination-sequence">
                {CALCINATION_STAGES.map((m, i) => (
                  <li key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-700/5 border border-blue-700/15 text-blue-700 text-[12px] font-bold">{i + 1}</span>
                    <p className="mt-3 text-[13px] font-bold text-slate-900 leading-snug">{m.step[lang]}</p>
                    <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">{m.text[lang]}</p>
                  </li>
                ))}
              </ol>
            </div>

            <div id="einordnung" className="mt-12 scroll-mt-24">
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900">{t('grundlagen.s2Title')}</h2>
              <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">{t('grundlagen.s2Body')}</p>
            </div>
            <div id="reaktion" className="mt-12 scroll-mt-24">
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900">{t('grundlagen.s3Title')}</h2>
              <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">
                {t('grundlagen.s3Body')}{' '}
                <Link to="/reaktion" className="font-semibold text-blue-700 hover:text-blue-800">
                  {lang === 'de' ? 'Mehr zur puzzolanischen Reaktion' : 'More on the pozzolanic reaction'}
                </Link>.
              </p>
            </div>

            {/* Pillar cluster links */}
            <div id="warum" className="mt-14 scroll-mt-24">
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900">{t('grundlagen.whyTitle')}</h2>
              <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">{t('grundlagen.whyIntro')}</p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {WHY_BENEFITS[lang].map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 rounded-lg bg-white border border-slate-200 px-4 py-3 text-[14px] text-slate-700">
                    <Check className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <h3 className="mt-9 mb-4 text-[15px] font-bold text-slate-900">{t('grundlagen.mechanismTitle')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {POZZOLAN_MECHANISM.map((m, i) => (
                  <div key={i} className="relative rounded-xl border border-slate-200 bg-white p-5">
                    <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-blue-700">{m.step[lang]}</span>
                    <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{m.text[lang]}</p>
                    {i < POZZOLAN_MECHANISM.length - 1 && (
                      <ArrowRight className="hidden md:block absolute top-1/2 -right-3 -translate-y-1/2 w-5 h-5 text-slate-300" aria-hidden="true" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div id="historie" className="mt-14 scroll-mt-24">
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900">{t('grundlagen.timelineTitle')}</h2>
              <p className="mt-4 mb-7 text-[15px] text-slate-600 leading-relaxed">{t('grundlagen.timelineIntro')}</p>
              <Timeline />
            </div>

            <div id="cluster" className="mt-14 scroll-mt-24">
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900">
                {lang === 'de' ? 'Metakaolin: Grundlagen, Eigenschaften und Anwendungen' : 'Metakaolin: fundamentals, properties and applications'}
              </h2>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CLUSTER.map((c, i) => (
                  <li key={i}>
                    <Link to={c.to} className="group flex items-center justify-between gap-3 p-3.5 rounded-lg bg-white border border-slate-200 hover:border-blue-300 hover:shadow-sm transition">
                      <span className="text-[13.5px] font-semibold text-slate-800 group-hover:text-blue-700 transition-colors">{c[lang]}</span>
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-600 shrink-0 transition-colors" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div id="faq" className="mt-14 scroll-mt-24">
              <FAQ items={FAQS.grundlagen[lang]} />
            </div>

            <div id="quellen" className="mt-14 scroll-mt-24">
              <SourcesBlock sources={t('grundlagen.sources')} />
            </div>

            <div className="mt-12">
              <RelatedTopics items={RELATED.grundlagen} />
            </div>
          </article>
        </div>
      </section>
      <ContactStrip />
    </div>
  );
};

export default Grundlagen;
